import { useApi, generateInitials } from '@shopexperts/shared';
import type { ComboboxGroupData } from '@shopexperts/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, type Ref, unref } from 'vue';

/**
 * Public predictive search over searchable page schemas (collections, experts,
 * services…). Item values are the pages' site-relative URLs, ready to store
 * as link hrefs.
 */
export function usePageSearch(search: Ref<string> | string) {
  const api = useApi();
  const searchValue = computed(() => unref(search).trim());

  const { data, isFetching, isError, error } = useQuery({
    queryKey: computed(() => ['page-predictive-search', searchValue.value]),
    queryFn: () => api.public.predictiveSearch.search({ query: { query: searchValue.value } }),
    enabled: computed(() => searchValue.value.length > 0),
    staleTime: 0,
  });

  const groupedData = computed<ComboboxGroupData[]>(() => {
    const groups = data.value?.groups ?? [];
    return groups
      .filter((group) => group.items.length > 0)
      .map((group): ComboboxGroupData => {
        const hasImages = group.has_images ?? false;
        return {
          id: group.group_label.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          label: group.group_label,
          showImage: hasImages,
          items: group.items.map((item) => ({
            value: item.url,
            label: item.label,
            image: item.image || undefined,
            initials: hasImages ? generateInitials(item.label) || '' : undefined,
            metadata: { url: item.url },
          })),
        };
      });
  });

  return { groupedData, isFetching, isError, error };
}
